import { useState, useEffect, useCallback } from 'react';

const useFavorites = (contractAccNumber) => {
  const getStorageKey = useCallback(() => {
    return contractAccNumber ? `dewaChatbotFavorites_${contractAccNumber}` : 'dewaChatbotFavorites';
  }, [contractAccNumber]);

  // Initialize localStorage overrides only (not the initial state)
  const [localStorageOverrides, setLocalStorageOverrides] = useState(() => {
    try {
      const storageKey = contractAccNumber ? `dewaChatbotFavorites_${contractAccNumber}` : 'dewaChatbotFavorites';
      const savedFavorites = localStorage.getItem(storageKey);
      const parsed = savedFavorites ? JSON.parse(savedFavorites) : {};
      console.log('useFavorites initialized with localStorage overrides:', parsed);
      return parsed;
    } catch (error) {
      console.error('Error loading favorites from localStorage:', error);
      return {};
    }
  });

  // Add a force update counter to trigger re-renders
  const [updateCounter, setUpdateCounter] = useState(0);

  // Listen for favoritesUpdated events from ChatContent
  useEffect(() => {
    const handleFavoritesUpdated = (event) => {
      console.log('useFavorites received favoritesUpdated event:', event.detail);
      
      const { storageKey, favorites } = event.detail;
      const currentStorageKey = getStorageKey();
      
      console.log('Comparing storage keys:', { received: storageKey, current: currentStorageKey });
      
      if (storageKey === currentStorageKey) {
        console.log('Storage keys match, updating localStorage overrides from:', localStorageOverrides, 'to:', favorites);
        setLocalStorageOverrides(favorites);
        setUpdateCounter(prev => prev + 1); // Force re-render
      }
    };

    console.log('useFavorites setting up event listener for:', getStorageKey());
    window.addEventListener('favoritesUpdated', handleFavoritesUpdated);

    return () => {
      window.removeEventListener('favoritesUpdated', handleFavoritesUpdated);
    };
  }, [getStorageKey, localStorageOverrides]);

  // Check if an offer is favorited: PRIORITY: 1. Initial offer data, 2. localStorage overrides
  const isFavorite = useCallback((offerUno, initialOfferDataValue) => {
    console.log(`=== isFavorite check for ${offerUno} ===`);
    console.log('Initial offer data value:', initialOfferDataValue);
    console.log('LocalStorage overrides:', localStorageOverrides);
    console.log('Has override for this offer:', localStorageOverrides.hasOwnProperty(offerUno));
    
    // If localStorage has an override (from backend confirmation), use that
    if (localStorageOverrides.hasOwnProperty(offerUno)) {
      const overrideValue = localStorageOverrides[offerUno];
      console.log(`Using localStorage override for ${offerUno}:`, overrideValue);
      return overrideValue;
    }
    
    // Otherwise use the initial offer data value (this is the primary source)
    const result = initialOfferDataValue === true;
    console.log(`Using initial offer data for ${offerUno}:`, result);
    return result;
  }, [localStorageOverrides, updateCounter]);

  // This function determines what to send to backend (no local updates)
  const toggleFavorite = useCallback((offerUno, initialOfferDataValue) => {
    // Get current effective state
    const currentState = isFavorite(offerUno, initialOfferDataValue);
    const newState = !currentState;
    
    console.log(`toggleFavorite ${offerUno}: ${currentState} -> ${newState} (sending to backend)`);
    
    // Return what should be sent to backend (but don't update local state)
    return newState;
  }, [isFavorite]);

  return {
    localStorageOverrides,
    isFavorite,
    toggleFavorite,
    updateCounter // Expose for debugging
  };
};

export default useFavorites;